import { PayloadAction } from '@reduxjs/toolkit';
import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import {
  approveDocumentRequest,
  getDocumentByEntityFailure,
  getDocumentByEntityForObjectFailure,
  getDocumentByEntityForObjectRequest,
  getDocumentByEntityForObjectSuccess,
  getDocumentByEntityRequest,
  getDocumentByEntitySuccess,
  getPresignedURLFailure,
  getPresignedURLRequest,
  getPresignedURLSuccess,
  rejectDocumentRequest,
  setApproveDocumentFailure,
  setApproveDocumentSuccess,
  setDocumentStoreFailure,
  setDocumentStoreSuccess,
  setDocumentUpdateFailure,
  setDocumentUpdateSuccess,
  setRejectDocumentFailure,
  setRejectDocumentSuccess,
  storeDocumentRequest,
  updateDocumentRequest,
} from 'redux/slice/agency-onbording/Document.slice';
import {
  ApproveDocumentRequest,
  GetDocumentByEntityRequest,
  GetPresignedURLRequest,
  RejectDocumentRequest,
  StoreDocumentRequest,
  UpdateDocumentRequest,
} from 'redux/types/Document.types';
import { agencyOnboarding, yucollectAdmin } from 'services/api/endPoints';
import { HTTPClient } from 'services/api/http-client';

const fetchDocumentsByEntityApi = async (entityId: string) => {
  const response = await HTTPClient.GetRequest(
    agencyOnboarding.GET_DOCUMENT_BY_ENTITY_ID(entityId)
  );
  return response;
};

const fetchPresignedURLApi = async (objectKey: string) => {
  const response = await HTTPClient.GetRequest(
    agencyOnboarding.GET_PRESIGNED_URL(objectKey)
  );
  return response;
};

function* storeDocument(action: PayloadAction<StoreDocumentRequest>): any {
  try {
    const { formData } = action.payload;
    const response = yield HTTPClient.PostRequest(
      agencyOnboarding.STORE_DOCUMENT,
      formData
    );
    yield put(setDocumentStoreSuccess(response));
  } catch (error) {
    yield put(setDocumentStoreFailure('Something went wrong!'));
  }
}

function* updateDocument(action: PayloadAction<UpdateDocumentRequest>): any {
  try {
    const { id, formData } = action.payload || {};
    const response = yield HTTPClient.PutRequest(
      agencyOnboarding.UPDATE_DOCUMENT(id),
      formData
    );
    yield put(setDocumentUpdateSuccess(response));
  } catch (error) {
    yield put(setDocumentUpdateFailure('Something went wrong!'));
  }
}

function* approveDocument(action: PayloadAction<ApproveDocumentRequest>): any {
  try {
    const { id } = action.payload || {};
    const response = yield HTTPClient.PutRequest(
      yucollectAdmin.APPROVE_DOCUMENT(id)
    );
    yield put(setApproveDocumentSuccess(response));
  } catch (error) {
    yield put(setApproveDocumentFailure('Something went wrong!'));
  }
}

function* rejectDocument(action: PayloadAction<RejectDocumentRequest>): any {
  try {
    const { id } = action.payload || {};
    const response = yield HTTPClient.PutRequest(
      yucollectAdmin.REJECT_DOCUMENT(id)
    );
    yield put(setRejectDocumentSuccess(response));
  } catch (error) {
    yield put(setRejectDocumentFailure('Something went wrong!'));
  }
}

function* getDocumentByEntity(
  action: PayloadAction<GetDocumentByEntityRequest>
): any {
  try {
    const { entityId } = action.payload || {};
    const response = yield call(fetchDocumentsByEntityApi, entityId);
    if (response && response.status === 200) {
      yield put(getDocumentByEntitySuccess(response.data));
    } else {
      yield put(getDocumentByEntityFailure('Something went wrong!'));
    }
  } catch (error) {
    yield put(getDocumentByEntityFailure('Something went wrong!'));
  }
}

function* getDocumentByEntityForObject(
  action: PayloadAction<GetDocumentByEntityRequest>
): any {
  try {
    const { entityId } = action.payload || {};
    const response = yield call(fetchDocumentsByEntityApi, entityId);

    if (response && response.status === 200) {
      yield put(
        getDocumentByEntityForObjectSuccess({
          data: response.data,
          entityId,
        })
      );
    } else {
      yield put(getDocumentByEntityForObjectFailure('Something went wrong!'));
    }
  } catch (error) {
    yield put(getDocumentByEntityForObjectFailure('Something went wrong!'));
  }
}

function* getPresignedURL(action: PayloadAction<GetPresignedURLRequest>): any {
  try {
    const { objectId } = action.payload || {};
    const response = yield call(fetchPresignedURLApi, objectId);
    if (response && response.status === 200) {
      yield put(getPresignedURLSuccess(response.data.replace(/\s/g, '')));
    } else {
      yield put(getPresignedURLFailure('Something went wrong!'));
    }
  } catch (error) {
    yield put(getPresignedURLFailure('Something went wrong!'));
  }
}

export function* watchStoreDocument() {
  yield takeLatest(storeDocumentRequest.type, storeDocument);
  yield takeLatest(updateDocumentRequest.type, updateDocument);
  yield takeLatest(approveDocumentRequest.type, approveDocument);
  yield takeLatest(rejectDocumentRequest.type, rejectDocument);
  yield takeLatest(getDocumentByEntityRequest.type, getDocumentByEntity);
  yield takeEvery(
    getDocumentByEntityForObjectRequest.type,
    getDocumentByEntityForObject
  );
  yield takeEvery(getPresignedURLRequest.type, getPresignedURL);
}
